'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import clsx from 'clsx';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LinkButton } from '@/components/ui/Button/LinkButton';
import { Icon } from '@/components/ui/Icon';
import styles from './DynamicLatestNews.module.scss';
import type { DynamicLatestNewsProps } from './DynamicLatestNews.types';
export type {
  DynamicLatestNewsProps,
  DynamicLatestNewsSlide,
  DynamicLatestNewsStaticContent,
} from './DynamicLatestNews.types';
import {
  DEFAULT_SLIDE_INTERVAL,
  EXIT_ANIMATION_DURATION,
  WHEEL_IMAGE_SRC,
} from './DynamicLatestNews.utils';

export function DynamicLatestNews({
  staticContent,
  slides,
  discoverCtaLabel,
  slideInterval = DEFAULT_SLIDE_INTERVAL,
}: Readonly<DynamicLatestNewsProps>) {
  const t = useTranslations('common');
  const aria = useAriaLabels();
  const resolvedDiscoverCtaLabel = discoverCtaLabel ?? t('discoverNow');
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const enteringRef = useRef<HTMLDivElement>(null);

  const totalSlides = slides.length;

  const advanceSlide = useCallback(() => {
    setPrevIndex(activeIndex);
    setActiveIndex((prev) => (prev + 1) % totalSlides);
    setAnimationKey((prev) => prev + 1);
  }, [activeIndex, totalSlides]);

  const goToSlide = useCallback(
    (index: number) => {
      if (index === activeIndex) return;
      setPrevIndex(activeIndex);
      setActiveIndex(index);
      setAnimationKey((prev) => prev + 1);
    },
    [activeIndex]
  );

  // Auto-slide timer (never pauses)
  useEffect(() => {
    if (totalSlides <= 1) return;

    timerRef.current = setTimeout(advanceSlide, slideInterval);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [activeIndex, totalSlides, slideInterval, advanceSlide]);

  // City name enter animation via double rAF
  useEffect(() => {
    const el = enteringRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.remove(styles.cityNameEntering);
        el.classList.add(styles.cityNameActive);
      });
    });
  }, [activeIndex]);

  // Clear prevIndex after exit animation completes
  useEffect(() => {
    if (prevIndex === null) return;
    const timeout = setTimeout(() => setPrevIndex(null), EXIT_ANIMATION_DURATION);
    return () => clearTimeout(timeout);
  }, [prevIndex]);

  if (!slides.length) return null;

  const activeSlide = slides[activeIndex];
  const exitingSlide = prevIndex !== null ? slides[prevIndex] : null;

  return (
    <section className={styles.section}>
      {/* Left Panel — Static content + wheel */}
      <div className={styles.leftPanel}>
        <Image
          src={staticContent.backgroundImage.src}
          alt={staticContent.backgroundImage.alt}
          fill
          className={styles.leftPanelBackground}
          sizes="60vw"
          priority
        />
        <div className={styles.leftPanelOverlay} />

        {/* Wheel */}
        <div className={styles.wheelContainer}>
          <Image
            src={WHEEL_IMAGE_SRC}
            alt="World clock wheel"
            fill
            className={styles.wheelImage}
            sizes="750px"
          />

          {/* City names inside wheel center */}
          <div className={styles.cityNamesViewport}>
            {/* Exiting city names */}
            {exitingSlide && (
              <div className={clsx(styles.cityNameSet, styles.cityNameExiting)}>
                <Icon name="chevron-down" size={12} className={styles.cityChevron} />
                <span className={styles.cityPrimary}>{exitingSlide.cityNames.primary}</span>
                <span className={styles.cityDivider} />
                <span className={styles.citySecondary}>{exitingSlide.cityNames.secondary}</span>
              </div>
            )}

            {/* Entering city names */}
            <div
              key={activeIndex}
              ref={enteringRef}
              className={clsx(styles.cityNameSet, styles.cityNameEntering)}
            >
              <Icon name="chevron-down" size={12} className={styles.cityChevron} />
              <span className={styles.cityPrimary}>{activeSlide.cityNames.primary}</span>
              <span className={styles.cityDivider} />
              <span className={styles.citySecondary}>{activeSlide.cityNames.secondary}</span>
            </div>
          </div>
        </div>

        {/* Static text content */}
        <div className={styles.leftPanelContent}>
          <span className={styles.subtitle}>{staticContent.subtitle}</span>
          <h2 className={styles.heading}>{staticContent.heading}</h2>
          <LinkButton
            href={staticContent.ctaUrl}
            variant="outline"
            color="white"
            className={styles.leftPanelCta}
          >
            {staticContent.ctaLabel}
          </LinkButton>
        </div>
      </div>

      {/* Right Panel — Slides + progress */}
      <div className={styles.rightPanel}>
        {/* Progress bar */}
        <div
          className={styles.progressBar}
          role="tablist"
          aria-label={aria.action('slideProgress')}
        >
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`Go to slide ${index + 1}`}
              className={clsx(styles.progressSegment, index < activeIndex && styles.completed)}
              onClick={() => goToSlide(index)}
            >
              {index === activeIndex && (
                <div
                  key={animationKey}
                  className={styles.progressFill}
                  style={{
                    animationDuration: `${slideInterval}ms`,
                  }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Active slide — hard cut */}
        <div className={styles.slideContainer}>
          <Image
            key={activeSlide.id}
            src={activeSlide.thumbnail.src}
            alt={activeSlide.thumbnail.alt}
            fill
            className={styles.slideImage}
            // CMS sources are usually landscape while this panel is portrait. A 100vw
            // candidate preserves crop detail without making mobile request a 200vw asset.
            sizes="100vw"
          />
          <div className={styles.slideOverlay} />

          <div className={styles.slideContent}>
            <span className={styles.timezoneLabel}>{activeSlide.timezoneLabel}</span>
            <h3 className={styles.articleTitle}>{activeSlide.articleTitle}</h3>
            <LinkButton
              href={activeSlide.articleUrl}
              variant="link"
              color="white"
              iconAfter={<Icon name="chevron-right" size={14} />}
              className={styles.discoverCta}
            >
              {resolvedDiscoverCtaLabel}
            </LinkButton>
          </div>
        </div>
      </div>
    </section>
  );
}

DynamicLatestNews.displayName = 'DynamicLatestNews';
